import React, { useEffect, useRef, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { MediaDA } from '../../services/admin/media.service';
import { authCompany } from '../../scripts/authentication.script';

interface Props {
  onAttach: (docId: string, docName: string) => void;
  onCancel: () => void;
}

const CopilotDocumentPicker: React.FunctionComponent<Props> = ({ onAttach, onCancel }) => {
  const { lang } = StateLanguage()
  const [tab, setTab] = useState<'search' | 'upload'>('search')
  const [docs, setDocs] = useState<any[] | null>(null)
  const [filter, setFilter] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const companyId = authCompany()?.id

  useEffect(() => {
    if (!companyId) return
    MediaDA.getDocuments(companyId, '', 'uploaded_at', 'desc', 1, 50).then((res) => {
      setDocs(res.status === 200 ? (res.data?.items ?? []) : [])
    }).catch(() => setDocs([]))
  }, [companyId])

  const filtered = (docs ?? []).filter((d: any) =>
    !filter || (d.name ?? d.file_name ?? '').toLowerCase().includes(filter.toLowerCase())
  )

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !companyId) return
    setUploading(true)
    setUploadError(false)
    try {
      const res = await MediaDA.uploadDocumentSmart(companyId, file)
      if ((res.status === 201 || res.status === 202) && res.data) {
        onAttach(res.data.id ?? res.data._id, res.data.name ?? res.data.original_filename ?? file.name)
      } else {
        setUploadError(true)
      }
    } catch {
      setUploadError(true)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="px-5 py-3 bg-light border-bottom">
      {/* Tabs */}
      <div className="d-flex gap-2 mb-3">
        <button
          type="button"
          className={`btn btn-sm flex-fill ${tab === 'search' ? 'btn-primary' : 'btn-light'}`}
          onClick={() => setTab('search')}
        >
          {lang.labels.copilotSearchExisting}
        </button>
        <button
          type="button"
          className={`btn btn-sm flex-fill ${tab === 'upload' ? 'btn-primary' : 'btn-light'}`}
          onClick={() => setTab('upload')}
        >
          {lang.labels.copilotUploadNew}
        </button>
      </div>

      {tab === 'search' && (
        <>
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder={lang.labels.copilotSearchPlaceholder}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {docs === null ? (
              <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm text-primary"></span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-muted text-center fs-8 py-3">{lang.labels.copilotNoDocuments}</div>
            ) : (
              filtered.map((d: any) => (
                <div
                  key={d.id ?? d._id}
                  className="d-flex align-items-center justify-content-between py-2 px-2 rounded cursor-pointer bg-hover-light-primary"
                  onClick={() => onAttach(d.id ?? d._id, d.name ?? d.file_name ?? d.original_filename ?? '')}
                >
                  <span className="fs-8 text-gray-800 text-truncate me-2">{d.name ?? d.file_name ?? '—'}</span>
                  <span className="badge badge-sm badge-light fs-9">{d.mime_type?.split('/')?.pop() ?? ''}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {tab === 'upload' && (
        <div className="text-center py-2">
          <input ref={fileRef} type="file" className="d-none" onChange={handleUpload} />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>{lang.labels.copilotUploading}</>
            ) : (
              lang.labels.copilotUploadNew
            )}
          </button>
          {uploadError && (
            <div className="text-danger fs-9 mt-2">{lang.labels.copilotUploadError}</div>
          )}
        </div>
      )}

      {/* Cancel */}
      <div className="text-end mt-2">
        <button type="button" className="btn btn-sm btn-light" onClick={onCancel}>{lang.labels.cancel}</button>
      </div>
    </div>
  )
}

export default CopilotDocumentPicker;
